import { Text } from '@/components/Text';
import i18n from '@/translations/config';
import { motion } from 'framer-motion';
import toast, { Toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FiArrowRight, FiZap } from 'react-icons/fi';
import { NavigateFunction } from 'react-router-dom';

interface PremiumToastProps {
  toastInstance: Toast;
  navigate: NavigateFunction;
  customLabel?: string | null;
}

export const PremiumToast = ({
  toastInstance,
  navigate,
  customLabel,
}: PremiumToastProps) => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{
        duration: 0.2,
        ease: 'easeOut',
      }}
      style={{
        willChange: 'transform, opacity',
        backfaceVisibility: 'hidden',
        perspective: 1000,
      }}
      className="pointer-events-auto w-full max-w-lg cursor-pointer rounded-lg border border-gray-200 bg-gray-50 shadow-lg"
      whileHover={{
        backgroundColor: '#f9fafb',
        transition: { duration: 0.15, ease: 'easeOut' },
      }}
      onClick={() => {
        navigate(`/${i18n.language}/dashboard/pricing`);
        toast.dismiss(toastInstance.id);
      }}
    >
      {/* Layout mobile : vertical */}
      <div className="flex flex-col gap-3 p-4 sm:hidden">
        <Text className="text-sm leading-relaxed text-gray-700">
          {customLabel ?? t('premiumToast:upgrade-message')}
        </Text>
        <div className="flex items-center justify-center gap-2">
          <span className="animate-gradient bg-gradient-to-r from-linko-light via-linko-dark to-linko-light bg-[length:200%_100%] bg-clip-text text-sm font-medium text-transparent">
            {t('premiumToast:upgrade-now')}
          </span>
          <motion.div
            animate={{ x: [0, 2, 0] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatType: 'loop',
              ease: 'easeInOut' as const,
              repeatDelay: 2,
            }}
          >
            <FiArrowRight className="size-3 text-linko-light" />
          </motion.div>
        </div>
      </div>

      {/* Layout desktop : horizontal */}
      <div className="hidden items-center justify-between gap-4 p-4 sm:flex">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <FiZap className="size-4 shrink-0 text-linko-light" />
          <Text className="text-sm leading-relaxed text-gray-700">
            {customLabel ?? t('premiumToast:upgrade-message')}
          </Text>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="animate-gradient whitespace-nowrap bg-gradient-to-r from-linko-light via-linko-dark to-linko-light bg-[length:200%_100%] bg-clip-text text-sm font-medium text-transparent">
            {t('premiumToast:upgrade-now')}
          </span>
          <motion.div
            animate={{ x: [0, 2, 0] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatType: 'loop',
              ease: 'easeInOut' as const,
              repeatDelay: 2,
            }}
          >
            <FiArrowRight className="size-3 text-linko-light" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};
